import { FC } from 'react';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import {
  AlertDialog,
  LoadingScreen,
  Typography,
} from '@taleemabad/dsm/components';

interface TaleemTrackerDashboardTemplateProps {
  iframeUrl: string;
  isLoading: boolean;
  hasError: boolean;
  isConnected: boolean;
  showNoInternetPopup: boolean;
  onIframeLoad: () => void;
  onIframeError: () => void;
  onNoInternetPopupClose: () => void;
}

export const TaleemTrackerDashboardTemplate: FC<
  TaleemTrackerDashboardTemplateProps
> = ({
  iframeUrl,
  isLoading,
  hasError,
  isConnected,
  showNoInternetPopup,
  onIframeLoad,
  onIframeError,
  onNoInternetPopupClose,
}) => {
  const renderContent = () => {
    if (hasError) {
      return (
        <ErrorContainer>
          <Typography variant="textHeadlineMediumOnSurface">
            Failed to Load Dashboard
          </Typography>
          <Typography variant="textBodyMediumOnSurfaceVariant">
            There was an error loading the dashboard. Please try again.
          </Typography>
        </ErrorContainer>
      );
    }

    if (isConnected) {
      return (
        <ContentWrapper>
          {isLoading && <LoadingScreen />}
          <DashboardContainer isVisible={!isLoading}>
            <DashboardIframe
              src={iframeUrl}
              width="100%"
              height="100%"
              title="NIETE Dashboard"
              onLoad={onIframeLoad}
              onError={onIframeError}
            />
          </DashboardContainer>
        </ContentWrapper>
      );
    }
  };

  return (
    <>
      {renderContent()}

      <AlertDialog
        title="No Internet Connection"
        description="Seems like your internet is not connected. Connect and try again."
        showPopup={showNoInternetPopup}
        onClosePopup={onNoInternetPopupClose}
      />
    </>
  );
};

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 180px);

  ${mq(BreakpointSizes.xs)} {
    height: calc(100vh - 160px);
  }
`;

const DashboardContainer = styled.div<{ isVisible: boolean }>`
  width: 100%;
  border-radius: var(--md-sys-shape-corner-large);
  overflow: hidden;
  box-shadow: var(--md-sys-elevation-level1);
  background-color: var(--md-sys-color-surface);
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  position: ${(props) => (props.isVisible ? 'static' : 'absolute')};
  top: 0;
  left: 0;
`;

const DashboardIframe = styled.iframe`
  border: none;
  display: block;
  border-radius: var(--md-sys-shape-corner-large);
  width: 100%;
  height: calc(100vh - 20px);
  ${mq(BreakpointSizes.xs)} {
    height: calc(100vh - 100px);
  }

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 180px);
  text-align: center;
  gap: var(--rk-spacing-md);
  background-color: var(--md-sys-color-surface);
  border-radius: var(--md-sys-shape-corner-large);
  padding: var(--rk-spacing-xl);

  ${mq(BreakpointSizes.xs)} {
    height: calc(100vh - 160px);
  }
`;
